import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import reportWebVitals from "./reportWebVitals";

// --- CSS --- //
import "./index.css";

// --- JS --- //
import "./UI/UI_Mode.js";

// --- AUTH --- //
import { AuthProvider, RoleBasedRoute } from "./utilities/auth/AuthContext.js";

// --- WEBSITE HEADER DETAILS --- //
import { Helmet } from "react-helmet"; // Import Helmet

// --- FILES & PAGES --- //
import Login from "./paths/Login";
import PrivateRoute from "./utilities/auth/PrivateRoute.js";
import MyProfile from "./paths/User/MyProfile.jsx";
import MyCerts from "./paths/User/MyCerts.jsx";
import Map from "./paths/User/Map.jsx";
import UserHeader from "./components/Headers/HeaderUser.jsx";
import GymHeader from "./components/Headers/HeaderGym.jsx";
import GymEmployeeDashboard from "./paths/Gym/GymEmployeeDashboard.jsx";
import GymCertify from "./paths/Gym/GymCertify.jsx";
import ForgotPassword from "./paths/ForgotPassword.jsx";
import SignUp from "./paths/SignUp.jsx";
import Home from "./paths/Public/Home.jsx";
import HeaderPublic from "./components/Headers/HeaderPublic.jsx";
import FooterPublic from "./components/Footers/FooterPublic.jsx";
import Climbers from "./paths/Public/Climbers.jsx";
import Gyms from "./paths/Public/Gyms.jsx";
import Pricing from "./paths/Public/Pricing.jsx";
import Contact from "./paths/Public/Contact.jsx";
import EmailVerification from "./paths/EmailVerification.jsx";
import Waitlist from "./paths/Public/Waitlist.jsx";

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <AuthProvider>
      <Router>
        <Routes>
          <Route
            path="/login"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | LOGIN</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <Login />
              </>
            }
          />

          {/* Public Routes */}
          {/* --- HOME LANDING PAGE --- */}
          <Route
            path="/"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | Home</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <HeaderPublic />
                <main className="main-content-body">
                  <Home />
                  <FooterPublic />
                </main>
              </>
            }
          />

          {/* --- WAITLIST PAGE --- */}
          <Route
            path="/waitlist"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | Join Waitlist</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <HeaderPublic />
                <main className="main-content-body">
                  <Waitlist />
                  <FooterPublic />
                </main>
              </>
            }
          />

          {/* --- INFO PAGES --- */}
          <Route
            path="/gyms"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | Gyms</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <HeaderPublic />
                <main className="main-content-body">
                  <Gyms />
                  <FooterPublic />
                </main>
              </>
            }
          />
          <Route
            path="/climbers"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | Climbers</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <HeaderPublic />
                <main className="main-content-body">
                  <Climbers />
                  <FooterPublic />
                </main>
              </>
            }
          />
          <Route
            path="/pricing"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | Pricing</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <HeaderPublic />
                <main className="main-content-body">
                  <Pricing />
                  <FooterPublic />
                </main>
              </>
            }
          />
          <Route
            path="/contact"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | Contact</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <HeaderPublic />
                <main className="main-content-body">
                  <Contact />
                  <FooterPublic />
                </main>
              </>
            }
          />

          {/* Public Access Route */}
          <Route
            path="/forgot-password"
            element={
              <RoleBasedRoute allowedRoles={["public"]} fallbackRoute="/login">
                <Helmet>
                  <title>VERT CERT | Forgot Password</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <ForgotPassword />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/sign-up"
            element={
              <RoleBasedRoute allowedRoles={["public"]} fallbackRoute="/login">
                <Helmet>
                  <title>VERT CERT | Sign Up</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <SignUp />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/verify-email"
            element={
              <>
                <Helmet>
                  <title>VERT CERT | Verify Email</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <EmailVerification />
              </>
            }
          />

          {/* Regular User Routes */}
          <Route
            path="/my-profile"
            element={
              <PrivateRoute>
                <RoleBasedRoute allowedRoles={["user"]} fallbackRoute="/login">
                  <Helmet>
                    <title>VERT CERT | My Profile</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <meta lang="en-us" />
                    <meta charSet="utf-8" />
                  </Helmet>
                  <UserHeader />
                  <main className="main-content-body">
                    <MyProfile />
                    <FooterPublic />
                  </main>
                </RoleBasedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-certs"
            element={
              <PrivateRoute>
                <RoleBasedRoute allowedRoles={["user"]} fallbackRoute="/login">
                  <Helmet>
                    <title>VERT CERT | My Certifications</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <meta lang="en-us" />
                    <meta charSet="utf-8" />
                  </Helmet>
                  <UserHeader />
                  <main className="main-content-body">
                    <MyCerts />
                    <FooterPublic />
                  </main>
                </RoleBasedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/my-map"
            element={
              <PrivateRoute>
                <Helmet>
                  <title>VERT CERT | My Map</title>
                  <meta name="viewport" content="width=device-width, initial-scale=1" />
                  <meta lang="en-us" />
                  <meta charSet="utf-8" />
                </Helmet>
                <RoleBasedRoute allowedRoles={["user"]} fallbackRoute="/login">
                  <UserHeader />
                  <main className="main-content-body">
                    <Map />
                    <FooterPublic />
                  </main>
                </RoleBasedRoute>
              </PrivateRoute>
            }
          />

          {/* Gym Access Routes */}
          <Route
            path="/gym-employees"
            element={
              <PrivateRoute>
                <RoleBasedRoute allowedRoles={["gymAccess"]} fallbackRoute="/login">
                  <Helmet>
                    <title>VERT CERT | Employee Dashboard</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <meta lang="en-us" />
                    <meta charSet="utf-8" />
                  </Helmet>
                  <GymHeader />
                  <main className="main-content-body">
                    <GymEmployeeDashboard />
                    <FooterPublic />
                  </main>
                </RoleBasedRoute>
              </PrivateRoute>
            }
          />

          <Route
            path="/certify"
            element={
              <PrivateRoute>
                <RoleBasedRoute allowedRoles={["gymAccess"]} fallbackRoute="/login">
                  <Helmet>
                    <title>VERT CERT | Certify</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <meta lang="en-us" />
                    <meta charSet="utf-8" />
                  </Helmet>
                  <GymHeader />
                  <main className="main-content-body">
                    <GymCertify />
                    <FooterPublic />
                  </main>
                </RoleBasedRoute>
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </AuthProvider>
  </React.StrictMode>
);

reportWebVitals();